/**
 * Waits for queued Vault work through the platform's shared job API.
 *
 * Copy, download, and connector execution use existing jobs rather than a
 * Vault-specific polling protocol. This helper normalizes terminal behavior so
 * hooks can report progress without duplicating scheduler semantics.
 */
import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import { fetchJobStatus } from '@/subsystems/jobs/services/jobs.service'
import type { JobStatus } from '@/subsystems/jobs/services/jobs.service'

const wait = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds))
const POLL_INTERVAL_MS = 2_000
const MAX_POLL_ATTEMPTS = 3_600

export async function waitForVaultJob(
  api: NodeApiClient,
  orgId: string,
  jobScopeId: string,
  jobId: string,
  onStatus?: (job: JobStatus) => void,
): Promise<void> {
  // cm-coding: A connector snapshot can span several bounded Lambda
  // continuations when one credential exposes many current objects. Keep one
  // UI action attached to the durable job for up to two hours so healthy work
  // is not reported as a browser timeout solely because of provider volume.
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    const job = await fetchJobStatus(api, orgId, jobScopeId, jobId)
    onStatus?.(job)
    if (job.status === 'COMPLETED') return
    if (job.status === 'ERROR') throw new Error(job.error ?? 'Vault job failed')
    await wait(POLL_INTERVAL_MS)
  }
  throw new Error('Vault job timed out')
}
